import { graphql } from 'gatsby';
import { GatsbyImage } from 'gatsby-plugin-image';
import React, { useState } from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';

import Partnership from '../models/Partnership/Partnership';
import PartnershipPropTypes from '../models/Partnership/PartnershipPropTypes';

import AwardCarousel from '../components/award-carousel.jsx';
import ExpertsSection from '../components/experts';
import FeaturedContentCard from '../components/featured-content-card';
import HowWeCanHelpSection from '../components/how-we-can-help-section';
import JsonLd from '../components/jsonld.jsx';
import Layout from '../components/layout';
import OfferingCard from '../components/offering-card';
import PointOfViewSection from '../components/point-of-view-section';
import RelatedInsights from '../components/related-insights';
import SEO from '../components/seo';
import Icon from '../components/icon';
import ContactForm from '../components/contact-form.jsx';
import RelatedCaseStudies from '../components/related-case-studies.jsx';
import CanonicalLink from '../components/canonical-link';

import { AnimateIn, Container, Grid, GridItem, Paper, TitleSet, media } from '../ui-components';

import renderRichText from '../utils/render-rich-text';
import getPageType from '../utils/get-page-type.jsx';

import CONTENT from '../static-content/partnerships.json';
import ImageWithTitleset from '../components/about-us/image-with-titleset.jsx';
import { isContentExcluded } from '../utils/is-content-excluded.jsx';
import { getSpaceId } from '../utils/geo-space-id-helpers.jsx';

const propTypes = {
  data: PartnershipPropTypes.isRequired,
  pageContext: PropTypes.any,
  path: PropTypes.string
};

/**
 * Component that renders a partnership or partnership offering
 *
 * @component PartnershipDetail
 *
 * @param {object} props
 *
 * @prop {object} data
 * @prop {object} pageContext
 * @prop {string} path
 *
 * @returns {JSX} returns partnership detail template
 */
const PartnershipDetail = props => {
  const [seoSlug, setSeoSlug] = useState('');
  const { data, pageContext, path } = props;
  const globalPartnershipReference =
    data?.contentfulPartnership?.localizationSlug === data?.globalPartnership?.slug && data?.globalPartnership;
  const partnership = new Partnership(data.contentfulPartnership, globalPartnershipReference);
  const {
    capabilitiesOverviewBlock,
    featuredContent,
    howWeCanHelp,
    partnershipOffering,
    partnershipOfferings,
    pointOfView,
    relatedCaseStudies,
    relatedInsights,
    solutionDetail
  } = partnership;
  const experts = data?.experts?.edges;

  const solutionDetailContent =
    solutionDetail &&
    data?.solutionDetails?.edges.find(solutionDetail => solutionDetail?.node?.parentPage?.slug === partnership?.slug)
      ?.node;
  const regionalSolutionDetailContent = data?.regionalSolutionDetails?.edges
    .filter(solutionDetail => solutionDetail?.node?.parentPageSlug === partnership?.slug)
    .find(solutionDetail => solutionDetail?.node?.spaceId === getSpaceId(pageContext?.space));
  const showSolutionDetail =
    solutionDetailContent &&
    !isContentExcluded(solutionDetailContent, pageContext?.space, regionalSolutionDetailContent);

  let pageType = getPageType(path);
  const isRegionalContent = pageContext.spaceId !== process.env.CONTENTFUL_GLOBAL_SPACE_ID;
  const handleSeoSlugChange = value => {
    setSeoSlug(value);
  };
  /**
   * Renders the SEO metatag and structured data elements
   *
   * @function renderSEO
   *
   * @returns {JSX}
   */
  const renderSEO = () => {
    return (
      <>
        <SEO
          title={partnership.title}
          description={partnership.snippet}
          slug={seoSlug}
          image={partnership.logo?.fluid?.images?.fallback?.src}
        />
        <CanonicalLink
          isRegionalContent={isRegionalContent}
          contentType={'partnerships'}
          contentSlug={partnership.slug}
          onSeoSlugChange={handleSeoSlugChange}
        />
        <JsonLd
          item={{
            '@context': 'https://schema.org',
            '@type': 'Article',
            name: partnership.title,
            image: partnership.logo?.fluid?.images?.fallback?.src
          }}
        />
      </>
    );
  };

  /**
   * Renders the Hero Section
   *
   * @function renderTitleSection
   *
   * @returns {JSX}
   */
  const renderTitleSection = () => {
    const { title, snippet } = partnership;

    return (
      <TitleSection>
        {partnership?.logo?.fluid && (
          <Logo loading="eager" fetchpriority="high" image={partnership.logo.fluid} alt={`${partnership.title} Logo`} />
        )}
        <TitleSet headingLevel={1} title={title} bodyText={snippet} />
      </TitleSection>
    );
  };

  /**
   * Renders the POV Section
   *
   * @function renderPOVBlock
   *
   * @returns {JSX}
   */
  const renderPOVBlock = () => {
    return <PointOfViewSection pointOfView={pointOfView} geoSpace={pageContext.space} />;
  };

  /**
   * Renders the How We Can Help section
   *
   * @function renderHowWeCanHelp
   *
   * @returns {JSX}
   */
  const renderHowWeCanHelp = () => {
    return (
      <HowWeCanHelpSection
        howWeCanHelp={howWeCanHelp}
        geoSpace={pageContext.space}
        pageType={pageType}
        renderIcon={partnershipOffering}
      />
    );
  };

  /**
   * Renders the Partnership Offerings section
   *
   * @function renderPartnershipOfferingsSection
   *
   * @returns {JSX}
   */
  const renderPartnershipOfferingsSection = () => {
    const { partnershipOfferingsLabel, partnershipOfferingsTitle, partnershipOfferingsRichText } = partnership;
    const partnershipOfferingCards = partnershipOfferings?.map(partnershipOffering => {
      return (
        <GridItem col={3} colxl={4} colmd={6} colsm={12} key={partnershipOffering.slug}>
          <OfferingCard
            title={partnershipOffering.title}
            content={partnershipOffering.snippet}
            layout={'withBorderTop'}
            slug={partnershipOffering.slug ? `/partnerships/${partnershipOffering.slug}` : null}
            geoSpace={pageContext.space}
          />
        </GridItem>
      );
    });

    return (
      <AnimateIn onScroll>
        <Container id="partnership-offerings-section" sectionContainer>
          <Icon horizontal={'left'} vertical={'top'} pageType={pageType} />
          <TitleSet
            label={partnershipOfferingsLabel}
            title={partnershipOfferingsTitle}
            bodyText={renderRichText(partnershipOfferingsRichText)}
          />
          <OfferingGrid partnershipOfferings={partnershipOfferings}>{partnershipOfferingCards}</OfferingGrid>
        </Container>
      </AnimateIn>
    );
  };

  /**
   * Renders the Certifications section
   *
   * @function renderCertificationsSection
   *
   * @returns {JSX}
   */
  const renderCertificationsSection = () => {
    const { certificationsLabel, certificationsTitle, certificationsRichText, certificationsImageCarousel } =
      partnership;

    if (certificationsImageCarousel) {
      return (
        <AnimateIn onScroll>
          <Container id="certifications-section" sectionContainer>
            <TitleSet
              label={certificationsLabel ? certificationsLabel : ''}
              title={certificationsTitle ? certificationsTitle : ''}
              bodyText={certificationsRichText ? renderRichText(certificationsRichText) : ''}
            />
            <AwardCarousel awards={certificationsImageCarousel} />
          </Container>
        </AnimateIn>
      );
    }
  };

  /**
   * Renders the Featured Content section
   *
   * @function renderFeaturedContent
   *
   * @returns {JSX}
   */
  const renderFeaturedContent = () => {
    return (
      <AnimateIn onScroll>
        <FeaturedContentContainer sectionContainer>
          {/* TODO 19966: To allow featuredContent to be a Case Study, update the featuredContent field query in the partnership fragment to include an on ContentfulCaseStudy case */}
          <FeaturedContentCard featuredContentRaw={featuredContent} />
        </FeaturedContentContainer>
      </AnimateIn>
    );
  };

  /**
   * Renders the Services and Service Lines section
   *
   * @function renderServiceLinesSection
   *
   * @returns {JSX}
   */
  const renderServiceLinesSection = () => {
    const { serviceOfferingsLabel, serviceOfferingsTitle, serviceOfferingsRichText, serviceLines } = partnership;

    if (serviceLines) {
      const serviceLineCards = serviceLines.map(serviceLine => {
        const content = serviceLine?.snippetRichJSON
          ? serviceLine.snippetRichJSON
          : serviceLine?.descriptionRichJSON
          ? serviceLine.descriptionRichJSON
          : '';

        return (
          <GridItem col={6} colsm={12} key={serviceLine.slug}>
            <OfferingCard
              title={serviceLine.title}
              content={content}
              layout={'wide'}
              slug={'/services/' + serviceLine.serviceSlug + '/' + serviceLine.slug}
              geoSpace={pageContext.space}
            />
          </GridItem>
        );
      });

      return (
        <ServiceOfferingsSectionWrapper id="service-offerings-section">
          <AnimateIn onScroll>
            <Container id="service-offerings-section" sectionContainer>
              <Icon horizontal={partnershipOffering ? 'left' : 'right'} vertical={'default'} pageType={pageType} />
              <TitleSet
                label={serviceOfferingsLabel ? serviceOfferingsLabel : ''}
                title={serviceOfferingsTitle ? serviceOfferingsTitle : ''}
                bodyText={serviceOfferingsRichText ? renderRichText(serviceOfferingsRichText) : ''}
              />
              <Grid>{serviceLineCards}</Grid>
            </Container>
          </AnimateIn>
        </ServiceOfferingsSectionWrapper>
      );
    }
  };

  /**
   * Renders the Experts section
   *
   * @function renderExpertsSection
   *
   * @returns {JSX}
   */
  const renderExpertsSection = () => {
    const { expertsTitle, expertsLabel, expertsRichText } = partnership;
    const experts = globalPartnershipReference
      ? data?.experts?.edges.concat(data?.globalExperts?.edges)
      : data?.experts?.edges;
    const refinedExperts = [];

    experts.forEach(expert => {
      expert.node.expertise?.forEach(field => {
        if (
          field?.sys?.contentType?.sys?.id === 'partnership' &&
          field?.title === (globalPartnershipReference?.title || partnership.title)
        ) {
          refinedExperts.push(expert.node);
        }
      });
    });

    if (refinedExperts.length) {
      return (
        <ExpertsSection
          expertsLabel={expertsLabel ? expertsLabel : CONTENT.EXPERTS__LABEL}
          expertsTitle={expertsTitle ? expertsTitle : CONTENT.EXPERTS__TITLE}
          expertsContent={expertsRichText ? expertsRichText : CONTENT.EXPERTS__CONTENT}
          experts={refinedExperts}
        />
      );
    }
  };

  /**
   * Renders the Related Insights section
   *
   * @function renderRelatedInsightsSection
   *
   * @returns {JSX}
   */
  const renderRelatedInsightsSection = () => {
    return (
      <>
        <RelatedInsights insights={relatedInsights} layout={partnershipOffering ? 'default' : 'side-by-side'} />
      </>
    );
  };

  /**
   * Renders the Related Case Studies section
   *
   * @function renderCaseStudiesSection
   *
   * @returns {JSX}
   */
  const renderCaseStudiesSection = () => {
    return (
      <>
        <RelatedCaseStudies caseStudies={relatedCaseStudies} />
      </>
    );
  };

  const renderCapabilitiesOverviewSection = () => {
    capabilitiesOverviewBlock['label'] = 'Capabilities';

    return (
      <AnimateIn onScroll>
        <PointOfViewSection pointOfView={capabilitiesOverviewBlock} secondary />
      </AnimateIn>
    );
  };

  const renderSolutionDetail = () => {
    return <ImageWithTitleset altMobileOrder contentBlock={solutionDetail} centerTextSection height={'360'} />;
  };

  /*function conditionally renders correct <Paper /> template depending on existence of case studies section*/
  const renderPartnershipDefault = () => {
    const partnershipSections = (
      <>
        {howWeCanHelp && renderHowWeCanHelp()}
        {partnershipOfferings && renderPartnershipOfferingsSection()}
        {showSolutionDetail && renderSolutionDetail()}
        {renderCertificationsSection()}
        {featuredContent && renderFeaturedContent()}
        {renderServiceLinesSection()}
        {experts && renderExpertsSection()}
      </>
    );

    const relatedInsightsAndContactForm = (
      <>
        {relatedInsights && renderRelatedInsightsSection()}
        <ContactForm geoSpace={pageContext?.space} slug={pageContext.slug} />
      </>
    );

    return (
      <Layout>
        {renderSEO()}
        <Paper bottom>{renderTitleSection()}</Paper>
        {pointOfView && renderPOVBlock()}
        {relatedCaseStudies ? (
          <>
            <Paper top>{partnershipSections}</Paper>
            <Paper bottom subtle />
            {renderCaseStudiesSection()}
            <Paper top subtle>
              {relatedInsightsAndContactForm}
            </Paper>
          </>
        ) : (
          <>
            <Paper top>
              {partnershipSections}
              {relatedInsightsAndContactForm}
            </Paper>
          </>
        )}
      </Layout>
    );
  };

  const renderPartnershipOffering = () => {
    const partnershipSectionsAndContactForm = (
      <>
        {experts && renderExpertsSection()}
        {renderServiceLinesSection()}
        {relatedInsights && renderRelatedInsightsSection()}
        <ContactForm geoSpace={pageContext.space} slug={pageContext.slug} />
      </>
    );

    return (
      <Layout>
        {renderSEO()}
        <Paper bottom>{renderTitleSection()}</Paper>
        {capabilitiesOverviewBlock && renderCapabilitiesOverviewSection()}
        {relatedCaseStudies ? (
          <>
            <Paper top>{howWeCanHelp && renderHowWeCanHelp()}</Paper>
            <Paper bottom subtle />
            {renderCaseStudiesSection()}
            <Paper top subtle>
              {partnershipSectionsAndContactForm}
            </Paper>
          </>
        ) : (
          <>
            <Paper top>
              {howWeCanHelp && renderHowWeCanHelp()}
              {partnershipSectionsAndContactForm}
            </Paper>
          </>
        )}
      </Layout>
    );
  };

  return partnershipOffering ? renderPartnershipOffering() : renderPartnershipDefault();
};

const OfferingGrid = styled(props => <Grid {...props} />)`
  #styled-grid-container {
    justify-content: ${({ partnershipOfferings }) => (partnershipOfferings.length < 4 ? 'center' : 'normal')};

    @media screen and (max-width: ${media.mediumBottom}) {
      justify-content: normal;
    }
  }
`;

const FeaturedContentContainer = styled(props => <Container {...props} />)`
  padding-left: 0;
  padding-right: 0;
`;

const TitleSection = styled(Container)`
  align-items: center;
  display: flex;
  flex-direction: column;
  gap: 32px;
  padding: 120px 60px;

  @media screen and (max-width: ${media.mediumTop}) {
    max-width: default;
    padding: 60px 36px;
  }
`;

const Logo = styled(GatsbyImage)`
  max-width: 200px;

  @media screen and (max-width: ${media.mediumTop}) {
    max-width: 150px;
  }
`;

const CapabilityGridItem = styled(GridItem)`
  gap: 4px;
  flex-direction: column;
`;

const ServiceOfferingsSectionWrapper = styled.div`
  /* This div is needed to clip the overflow of the icons */
  overflow: clip visible;
`;

PartnershipDetail.propTypes = propTypes;

export default PartnershipDetail;

export const pageQuery = graphql`
  query Partnership($slug: String!, $spaceId: String!, $globalSpaceId: String!, $globalPartnershipId: String) {
    contentfulPartnership(slug: { eq: $slug }, spaceId: { eq: $spaceId }) {
      ...CompletePartnership
    }
    globalPartnership: contentfulPartnership(
      spaceId: { eq: $globalSpaceId }
      contentful_id: { eq: $globalPartnershipId }
    ) {
      ...CompletePartnership
    }
    solutionDetails: allContentfulSolutionDetail(filter: { spaceId: { eq: $globalSpaceId } }) {
      edges {
        node {
          exclusionList {
            australia
            germany
            us
            uk
            india
          }
          parentPage {
            ... on ContentfulPartnership {
              slug
            }
          }
        }
      }
    }
    regionalSolutionDetails: allContentfulSolutionDetail {
      edges {
        node {
          parentPageSlug
          spaceId
        }
      }
    }
    experts: allContentfulPerson(filter: { spaceId: { eq: $spaceId }, expert: { eq: true } }) {
      edges {
        node {
          ...CompletePerson
        }
      }
    }
    globalExperts: allContentfulPerson(filter: { spaceId: { eq: $globalSpaceId }, expert: { eq: true } }) {
      edges {
        node {
          ...CompletePerson
        }
      }
    }
  }
`;
